import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import { AuthenticatedUser, LoginApiResponse } from '@repo/shared/domain';
import { Logo } from '@repo/components';
import request from '@nineeng/request';
import { QRCode } from '@nineeng/form-components';
import { LoadingButton } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { checkSSOAvailable, getDefaultExperience } from 'src/api/authentication';

export interface Credentials {
	email?: string;
	password?: string;
	twoFactorCode?: string;
	dataURI?: string;
}

export interface Errors {
	email?: string;
	password?: string;
	twoFactorCode?: string;
	topLevel?: string;
}

interface CredentialsComponentProps {
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	credentials: Credentials;
	login: (e?: React.FormEvent) => void | Promise<void>;
	loading: boolean;
	loginSSO: () => void;
	ssoAvailable: boolean;
	errors: Errors;
}

const ConfigureTwoFactorBox = ({
	credentials,
	errors = {},
	onChange,
	login,
	loading = false,
}: {
	credentials: Credentials;
	errors: Errors;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	login: () => void;
	loading?: boolean;
}) => {
	return (
		<form style={{ width: '100%' }} onSubmit={login}>
			<div
				style={{
					fontSize: '0.75rem',
					opacity: 0.8,
					marginBottom: '1rem',
					color: 'white',
				}}
			>
				You do not have two factor authentication enabled. Scan the QR code with your authenticator app and enter the
				code below. This will setup two factor authentication for your account.
			</div>
			<div style={{ backgroundColor: 'white', padding: '1rem' }}>
				<QRCode data={credentials.dataURI || ''} style={{ width: '100%' }} />
			</div>

			<TextField
				id="twoFactorCode"
				name="twoFactorCode"
				label="Two Factor Code"
				type="text"
				margin="dense"
				fullWidth={true}
				variant="outlined"
				value={credentials.twoFactorCode || ''}
				onChange={onChange}
				error={!!errors.twoFactorCode}
				helperText={errors.twoFactorCode}
				sx={{ marginBottom: 2, marginTop: 2 }}
				inputProps={{
					autocomplete: 'one-time-code',
				}}
				autoFocus
			/>
			{!!errors.topLevel && (
				<Alert severity="error" sx={{ marginBottom: 2 }}>
					{errors.topLevel}
				</Alert>
			)}

			<LoadingButton
				color="secondary"
				variant="outlined"
				fullWidth={true}
				onClick={login}
				loading={loading}
				type="submit"
				className="h-[56px]"
			>
				Authenticate
			</LoadingButton>
		</form>
	);
};

const TwoFactorBox = ({
	login,
	errors,
	onChange,
	credentials,
	loading = false,
}: {
	login: () => void;
	errors: Errors;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	credentials: Credentials;
	loading?: boolean;
}) => {
	return (
		<form style={{ width: '100%' }} method="POST" onSubmit={(e) => e.preventDefault()}>
			<TextField
				id="twoFactorCode"
				name="twoFactorCode"
				label="Two Factor Code"
				type="text"
				margin="dense"
				fullWidth={true}
				variant="outlined"
				value={credentials.twoFactorCode || ''}
				onChange={onChange}
				error={!!errors.twoFactorCode}
				helperText={errors.twoFactorCode}
				sx={{ marginBottom: 2, marginTop: 2 }}
				autoFocus
			/>
			{!!errors.topLevel && (
				<Alert severity="error" sx={{ marginBottom: 2 }}>
					{errors.topLevel}
				</Alert>
			)}

			<LoadingButton
				color="secondary"
				type="submit"
				variant="outlined"
				fullWidth={true}
				onClick={login}
				loading={loading}
				className="h-[56px] mt-4"
			>
				Authenticate
			</LoadingButton>
		</form>
	);
};

function CredentialsComponent({
	onChange,
	credentials,
	login,
	loading,
	loginSSO,
	ssoAvailable,
	errors,
}: CredentialsComponentProps) {
	return (
		<form className="w-full" method="POST" onSubmit={(e) => e.preventDefault()}>
			<TextField
				id="email"
				name="email"
				label="Email"
				type="email"
				margin="dense"
				fullWidth={true}
				autoFocus
				variant="outlined"
				value={credentials.email || ''}
				onChange={onChange}
				error={!!errors.email}
				helperText={errors.email}
				sx={{ marginBottom: 2 }}
				autoComplete="email"
				focused
			/>

			<TextField
				id="password"
				name="password"
				label="Password"
				type="password"
				margin="dense"
				fullWidth={true}
				variant="outlined"
				value={credentials.password || ''}
				onChange={onChange}
				error={!!errors.password}
				helperText={errors.password}
				sx={{ marginBottom: 2 }}
				autoComplete="current-password"
				focused
			/>
			<div className="mb-6">
				<Link
					to="/authentication/forgot-password"
					search={{
						email: credentials.email || '',
					}}
					className="text-white text-sm underline"
				>
					Forgot password?
				</Link>
			</div>

			{!!errors.topLevel && (
				<Alert severity="error" sx={{ marginBottom: 2 }}>
					{errors.topLevel}
				</Alert>
			)}

			<LoadingButton
				color="secondary"
				variant="outlined"
				fullWidth={true}
				onClick={login}
				loading={loading}
				type="submit"
				className="h-[56px]"
			>
				Login
			</LoadingButton>

			{ssoAvailable && (
				<LoadingButton color="primary" variant="outlined" fullWidth={true} onClick={loginSSO} className="h-[56px] mt-4">
					Login with SSO
				</LoadingButton>
			)}
		</form>
	);
}

type ScreenState = 'credentials' | 'two-factor' | 'configure-two-factor';

const initialErrorState: Errors = {
	email: '',
	password: '',
	twoFactorCode: '',
	topLevel: '',
};

const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
const determineRedirect = async (user: AuthenticatedUser, from = '/') => {
	const redirect = from.includes('_next') ? '/' : from;
	const userPermissions = user.permissions || [];

	if (!redirect || redirect === '/') {
		if (userPermissions.includes(PERMISSIONS.adminView) || userPermissions.includes(PERMISSIONS.siteAdmin)) {
			const experience = await getDefaultExperience();
			if (experience === '2.0') return '/app/admin/';
			return '/';
		}
		if (userPermissions.includes(PERMISSIONS.personalView)) {
			return '/personal';
		}
		if (userPermissions.includes(PERMISSIONS.companyView)) {
			return '/company';
		}
		if (userPermissions.includes(PERMISSIONS.supervisorView)) {
			return '/supervisor';
		}
		if (userPermissions.includes(PERMISSIONS.partnerCompanyView)) {
			return  '/partner';
		}

		return 'none';
	}

	return redirect || '';
};

interface LoginSearch {
	redirect?: string;
	ssoError?: boolean;
}
//todo: should redirect if user is already authenticated
export const Route = createFileRoute('/authentication/login')({
	validateSearch: (search: Record<string, unknown>): LoginSearch => {
		return {
			// Route guards / getLoginUrl pass `redirect`; guardedClient's 401 handler passes `from`.
			redirect: (search.redirect as string) || (search.from as string) || undefined,
			ssoError: search.ssoError === 'true' || false,
		};
	},
	loader: async () => {
		try {
			const { ssoAvailable } = await checkSSOAvailable();
			return { ssoAvailable };
		} catch (error) {
			return { ssoAvailable: false };
		}
	},
	component: Login,
});

function Login() {
	const { redirect, ssoError } = Route.useSearch();
	const { ssoAvailable } = Route.useLoaderData();

	const [screen, setScreen] = useState<ScreenState>('credentials');
	const [credentials, setCredentials] = useState<Credentials>({});
	const [errors, setErrors] = useState<Errors>({ ...initialErrorState });
	const [loading, setLoading] = useState<boolean>(false);

	const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
		if (target.name === 'email') {
			return setCredentials({
				...credentials,
				[target.name]: target.value.trim(),
			});
		}

		return setCredentials({
			...credentials,
			[target.name]: target.value,
		});
	};

	const login = async (e?: React.FormEvent) => {
		if (e) {
			e.preventDefault();
		}
		try {
			setErrors({ ...initialErrorState });
			setLoading(true);
		try {
			const { body } = await request.post('/api/auth/login', credentials);
			const { sessionUser } = body as LoginApiResponse;
			const redirectPath = await determineRedirect(sessionUser, redirect);
				if (redirectPath === 'none') {
					setErrors({
						topLevel: 'You do not have the correct permissions to access this page.',
					});
				}

				window.location.href = redirectPath;
			} catch (err: unknown) {
				console.log('err is: ', err);
				const error = err as Error & { body?: { message?: string } };
				setErrors({
					topLevel: error.body?.message || error.message || 'Login failed. Please try again.',
				});
			}
		} catch (err: unknown) {
			console.log('err is: ', err);
			const error = err as Error & { body?: { message?: string } };
			setErrors({
				topLevel: error.body?.message || error.message || 'Login failed. Please try again.',
			});
		} finally {
			setLoading(false);
		}
	};

	const loginSSO = () => {
		window.location.href = '/api/auth/sso/redirect' + (redirect ? '?returnTo=' + encodeURIComponent(redirect) : '');
	};

	const checkCredentials = async (e?: React.FormEvent) => {
		if (e) {
			e.preventDefault();
		}
		if (!credentials.email) {
			return setErrors({
				email: 'Email is required to login!',
			});
		}

		if (!credentials.password) {
			return setErrors({
				password: 'Password is required',
			});
		}

		try {
			setErrors({ ...initialErrorState });
			setLoading(true);
			const { body } = await request.post('/api/auth/check-two-factor', credentials);

			const { twoFactor, dataURI, twoFactorRequired } = body as {
				twoFactor: boolean;
				dataURI: string;
				twoFactorRequired: boolean;
			};

			if (twoFactor) {
				setScreen('two-factor');
			} else if (twoFactorRequired) {
				setCredentials({ ...credentials, dataURI });
				setScreen('configure-two-factor');
			} else {
				await login();
			}
		} catch (err: unknown) {
			const error = err as Error & { body?: { message?: string } };
			setErrors({
				topLevel: error.body?.message || error.message || 'Authentication failed. Please try again.',
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="absolute left-0 bg-[#101820] right-0 top-0 bottom-0 flex">
			<div className="w-[440px] max-w-full h-full flex flex-col justify-center items-end p-4">
				<Logo variant="full" style={{ width: 80, marginBottom: 64 }} />

				{screen === 'credentials' && (
					<CredentialsComponent
						onChange={handleChange}
						credentials={credentials}
						login={checkCredentials}
						loading={loading}
						loginSSO={loginSSO}
						ssoAvailable={ssoAvailable}
						errors={errors}
					/>
				)}
				{screen === 'configure-two-factor' && (
					<ConfigureTwoFactorBox
						credentials={credentials}
						errors={errors}
						login={login}
						loading={loading}
						onChange={handleChange}
					/>
				)}
				{screen === 'two-factor' && (
					<TwoFactorBox
						credentials={credentials}
						errors={errors}
						login={login}
						loading={loading}
						onChange={handleChange}
					/>
				)}
				{ssoError && (
					<Alert severity="error" sx={{ marginTop: 2, width: '100%' }}>
						Unable to login through SSO
					</Alert>
				)}
			</div>
			<div className="w-[calc(100%-440px)] h-full bg-cover bg-center bg-[url('/api/public/panorama')]" />
		</div>
	);
}
